import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { logout } from '../api/auth';
import {
  createCompany,
  createJoinRequest,
  fetchMyCompanies,
  searchCompanies,
} from '../api/company';
import { useAuthStore } from '../store/authStore';
import { useCompanyStore } from '../store/companyStore';
import { Company, CompanySearchItem } from '../types/company';
import { companyRoleLabel } from '../types/companyRole';
import './CompanySelectPage.css';

import { reportError } from '../utils/notify';
type Mode = 'list' | 'create' | 'join';

function companyInitial(name: string) {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  return trimmed.slice(0, 1).toUpperCase();
}

function CompanySelectPage() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setCompany = useCompanyStore((state) => state.setCompany);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState<Mode>('list');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [companyName, setCompanyName] = useState('');
  const [businessNumber, setBusinessNumber] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');

  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState<CompanySearchItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanySearchItem | null>(null);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const list = await fetchMyCompanies();
      setCompanies(list);
      setError('');
    } catch {
      reportError(setError, '회사 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadCompanies();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (mode !== 'join') {
      return;
    }

    const keyword = searchKeyword.trim();
    if (keyword.length < 1) {
      setSearchResults([]);
      setError('');
      return;
    }

    // 자동완성으로 선택한 직후엔 재검색하지 않음
    if (selectedCompany && selectedCompany.companyName === keyword) {
      return;
    }

    const timer = window.setTimeout(async () => {
      try {
        setSearching(true);
        setError('');
        const results = await searchCompanies(keyword);
        setSearchResults(results);
      } catch (err: unknown) {
        setSearchResults([]);
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '회사 검색에 실패했습니다.';
        reportError(setError, message);
      } finally {
        setSearching(false);
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [searchKeyword, mode, selectedCompany]);

  const filteredHint = useMemo(() => {
    if (mode !== 'join') return '';
    if (!searchKeyword.trim()) return '회사명을 입력하면 검색 결과가 표시됩니다.';
    if (searching) return '검색 중...';
    if (searchResults.length === 0) return '검색 결과가 없습니다.';
    return '';
  }, [mode, searchKeyword, searching, searchResults.length]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const enterCompany = (company: Company) => {
    setCompany({
      companyId: company.companyId,
      companyName: company.companyName,
      role: company.role,
      plan: company.plan ?? null,
    });
    navigate('/app', { replace: true });
  };

  const resetCreateForm = () => {
    setCompanyName('');
    setBusinessNumber('');
    setPhoneNumber('');
    setEmail('');
    setAddress('');
  };

  const resetJoinForm = () => {
    setSearchKeyword('');
    setSearchResults([]);
    setSelectedCompany(null);
  };

  const openCreate = () => {
    setMode('create');
    setError('');
    setSuccess('');
  };

  const openJoin = () => {
    setMode('join');
    setError('');
    setSuccess('');
  };

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      const created = await createCompany({
        companyName,
        businessNumber,
        phoneNumber,
        email,
        address,
      });
      setCompanies((prev) => [...prev, created]);
      setMode('list');
      resetCreateForm();
      setError('');
      setSuccess('');
      enterCompany(created);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '회사 생성에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoinRequest = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedCompany) {
      reportError(setError, '참가할 회사를 선택해 주세요.');
      return;
    }

    try {
      setSubmitting(true);
      await createJoinRequest(selectedCompany.companyId);
      setSuccess(`'${selectedCompany.companyName}' 회사에 참가 요청을 보냈습니다.`);
      setError('');
      resetJoinForm();
      setMode('list');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '참가 요청에 실패했습니다.';
      reportError(setError, message);
      setSuccess('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const modeTitle =
    mode === 'create' ? '회사 생성' : mode === 'join' ? '회사 참가' : '회사 선택';
  const modeSubtitle =
    mode === 'create'
      ? '새 회사 정보를 입력해 생성하세요.'
      : mode === 'join'
        ? '회사명을 검색해 참가 요청을 보내세요.'
        : '입장할 회사를 선택하거나, 생성·참가하세요.';

  return (
    <div className="company-page">
      <div className="company-page__panel">
        <div className="company-page__topbar">
          <span className="company-page__logo">
            <em>Line</em>log
          </span>
          <button type="button" className="company-page__logout" onClick={handleLogout}>
            로그아웃
          </button>
        </div>

        <header className="company-page__header">
          <h1>{modeTitle}</h1>
          <p>{modeSubtitle}</p>
        </header>

        {error && <p className="company-page__banner is-error">{error}</p>}
        {success && <p className="company-page__banner is-success">{success}</p>}

        <div key={mode} className="company-page__body">
          {mode === 'list' && (
            <>
              {!loading && companies.length > 0 && (
                <div className="company-page__actions">
                  <button type="button" className="company-page__primary" onClick={openCreate}>
                    + 회사 생성
                  </button>
                  <button type="button" className="company-page__secondary" onClick={openJoin}>
                    참가하기
                  </button>
                </div>
              )}

              {loading ? (
                <p className="company-page__hint">불러오는 중...</p>
              ) : companies.length === 0 ? (
                <div className="company-page__empty">
                  <p className="company-page__empty-title">아직 참여 중인 회사가 없습니다</p>
                  <p>회사를 생성하거나 참가 요청을 보내 주세요.</p>
                  <div className="company-page__actions is-empty">
                    <button type="button" className="company-page__primary" onClick={openCreate}>
                      + 회사 생성
                    </button>
                    <button type="button" className="company-page__secondary" onClick={openJoin}>
                      참가하기
                    </button>
                  </div>
                </div>
              ) : (
                <ul className="company-page__list">
                  {companies.map((company) => (
                    <li key={company.companyId}>
                      <button
                        type="button"
                        className="company-page__item"
                        onClick={() => enterCompany(company)}
                      >
                        <span className="company-page__avatar" aria-hidden>
                          {companyInitial(company.companyName)}
                        </span>
                        <span className="company-page__item-main">
                          <strong>{company.companyName}</strong>
                          <span>
                            {company.deletionPending
                              ? `삭제 예정 · ${company.deletionScheduledAt?.slice(0, 10) || '-'}`
                              : company.businessNumber || '사업자번호 없음'}
                          </span>
                        </span>
                        <span className="company-page__item-meta">
                          <em>
                            {company.deletionPending
                              ? '삭제 유예'
                              : companyRoleLabel(company.role)}
                          </em>
                          <span className="company-page__enter">
                            {company.deletionPending ? '복구/입장' : '입장'}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {mode === 'create' && (
            <form className="company-page__form" onSubmit={handleCreate}>
              <label>
                회사명
                <input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                />
              </label>
              <label>
                사업자번호
                <input
                  value={businessNumber}
                  onChange={(e) => setBusinessNumber(e.target.value)}
                  placeholder="000-00-00000"
                  required
                />
              </label>
              <label>
                전화번호
                <input
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  required
                />
              </label>
              <label>
                회사 이메일
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>
              <label>
                주소
                <input value={address} onChange={(e) => setAddress(e.target.value)} />
              </label>
              <div className="company-page__form-actions">
                <button type="submit" className="company-page__primary" disabled={submitting}>
                  {submitting ? '생성 중...' : '생성하고 입장'}
                </button>
                <button
                  type="button"
                  className="company-page__secondary"
                  onClick={() => {
                    setMode('list');
                    resetCreateForm();
                    setError('');
                  }}
                >
                  뒤로
                </button>
              </div>
            </form>
          )}

          {mode === 'join' && (
            <form className="company-page__form" onSubmit={handleJoinRequest}>
              <label>
                회사명 검색
                <input
                  value={searchKeyword}
                  onChange={(e) => {
                    setSearchKeyword(e.target.value);
                    setSelectedCompany(null);
                  }}
                  placeholder="회사명 입력"
                  autoFocus
                />
              </label>

              {filteredHint && <p className="company-page__hint">{filteredHint}</p>}

              {searchResults.length > 0 && (
                <ul className="company-page__suggest">
                  {searchResults.map((item) => (
                    <li key={item.companyId}>
                      <button
                        type="button"
                        className={`company-page__suggest-item${
                          selectedCompany?.companyId === item.companyId ? ' is-selected' : ''
                        }${item.alreadyJoined ? ' is-joined' : ''}`}
                        disabled={item.alreadyJoined}
                        onClick={() => {
                          if (item.alreadyJoined) return;
                          setSelectedCompany(item);
                          setSearchKeyword(item.companyName);
                          setSearchResults([]);
                        }}
                      >
                        <span className="company-page__avatar is-sm" aria-hidden>
                          {companyInitial(item.companyName)}
                        </span>
                        <span className="company-page__item-main">
                          <strong>{item.companyName}</strong>
                          <span>
                            {item.businessNumber}
                            {item.alreadyJoined ? ' · 이미 참여 중' : ''}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {selectedCompany && (
                <div className="company-page__selected">
                  <span className="company-page__avatar is-sm" aria-hidden>
                    {companyInitial(selectedCompany.companyName)}
                  </span>
                  <div>
                    <span className="company-page__selected-label">선택됨</span>
                    <strong>{selectedCompany.companyName}</strong>
                    <span>{selectedCompany.businessNumber}</span>
                  </div>
                </div>
              )}

              <div className="company-page__form-actions">
                <button
                  type="submit"
                  className="company-page__primary"
                  disabled={submitting || !selectedCompany}
                >
                  {submitting ? '요청 중...' : '참가 요청'}
                </button>
                <button
                  type="button"
                  className="company-page__secondary"
                  onClick={() => {
                    setMode('list');
                    resetJoinForm();
                    setError('');
                  }}
                >
                  뒤로
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default CompanySelectPage;
